import { useCallback, useMemo } from 'react'

import useUrlState from '@ahooksjs/use-url-state'
import { TimeRange, toURLTimeRange, urlToTimeRange } from '@tidbcloud/uikit/biz'
import { ProTableOptions } from '@tidbcloud/uikit/biz'

import { DEFAULT_TIME_RANGE } from '../utils/helpers'
import { ListUrlState, Pagination } from '../utils/type'

const DEFAULT_PAGE_SIZE = 20

export const useStatementListUrlState = () => {
  const [queryParams, setQueryParams] = useUrlState<ListUrlState>()

  //////////////////////////

  const timeRange = useMemo(() => {
    const { from, to } = queryParams
    if (from && to) {
      return urlToTimeRange({ from, to })
    }
    return DEFAULT_TIME_RANGE
  }, [queryParams.from, queryParams.to])

  const setTimeRange = useCallback(
    (newTimeRange: TimeRange) => {
      setQueryParams({ ...toURLTimeRange(newTimeRange), cur_page: undefined })
    },
    [setQueryParams]
  )

  //////////////////////////

  const kinds = useMemo(() => {
    if (!queryParams.kinds) {
      return []
    }
    return queryParams.kinds.split(',')
  }, [queryParams.kinds])

  const setKinds = useCallback(
    (newKinds: string[]) => {
      setQueryParams({
        kinds: newKinds.join(',') || undefined,
        cur_page: undefined
      })
    },
    [setQueryParams]
  )

  //////////////////////////

  const search = queryParams.search
  const setSearch = useCallback(
    (newSearch: string) => {
      setQueryParams({ search: newSearch || undefined, cur_page: undefined })
    },
    [setQueryParams]
  )

  //////////////////////////

  // TODO: extract to reuse
  const sortRules = useMemo(() => {
    const rules = (queryParams.sort || '-sum_latency')
      .split(',')
      .filter((s: string) => s !== '')
      .map((s: string) => {
        if (s.startsWith('-')) {
          return {
            id: s.slice(1),
            desc: true
          }
        }
        return {
          id: s,
          desc: false
        }
      })
    return rules
  }, [queryParams.sort])

  type onSortChangeFn = Required<ProTableOptions>['onSortingChange']

  const setSortRules = useCallback<onSortChangeFn>(
    (updater) => {
      const newSort =
        typeof updater === 'function' ? updater(sortRules) : updater
      if (newSort === sortRules) {
        return
      }
      const sort = newSort
        .map((rule: { id: string; desc: boolean }) => {
          if (rule.desc) {
            return `-${rule.id}`
          }
          return rule.id
        })
        .join(',')
      setQueryParams({ sort: sort || undefined, cur_page: undefined })
    },
    [setQueryParams, sortRules]
  )

  //////////////////////////

  const pagination = useMemo<Pagination>(() => {
    return {
      curPage: Number(queryParams.cur_page) || 1,
      pageSize: Number(queryParams.page_size) || DEFAULT_PAGE_SIZE
    }
  }, [queryParams.cur_page, queryParams.page_size])

  const setPagination = useCallback(
    (newPagination: Pagination) => {
      setQueryParams({
        cur_page:
          newPagination.curPage === 1 ? undefined : newPagination.curPage,
        page_size:
          newPagination.pageSize === DEFAULT_PAGE_SIZE
            ? undefined
            : newPagination.pageSize
      })
    },
    [setQueryParams]
  )

  //////////////////////////

  return {
    timeRange,
    setTimeRange,

    kinds,
    setKinds,

    search,
    setSearch,

    sortRules,
    setSortRules,

    pagination,
    setPagination
  }
}
